import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  AlertTriangle,
  Check,
  ChevronDown,
  Link2,
  Paperclip,
  Pencil,
  Plus,
  ShieldCheck,
} from 'lucide-react';
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@roger/ui-components';
import {
  getAuthorizationEvaluation,
  getPatientVisits,
  listEpisodeAuthorizations,
  listPatientInsurances,
  setVisitAuthorization,
} from '../../services/api';
import type {
  AuthorizationError,
  AuthorizationEvaluationResponse,
  AuthorizationResponse,
  AuthorizationStatus,
  VisitResponse,
} from '../../services/api';
import type { PatientInsuranceResponse } from '../../services/api/types.gen';
import { AuthorizationDialog } from './AuthorizationDialog';
import { AUTHORIZATION_STATUS_OPTIONS } from './authorizationConstants';
import {
  formatDateToMMDDYYYY,
  parseISOToLocalDate,
} from '../../utils/dateUtils';
import { scrollToField } from '../../utils/scrollToField';
import { errorToAnchor } from '../../utils/authChecks';
import { getBusinessErrorMessage } from '../../utils/apiErrorDetail';
import {
  DISCIPLINE_VISIT_FIELDS,
  authorizationLabel,
} from '../../utils/authorizationBudget';
import { useToast } from '../../hooks/useToastContext';
import { AutoSavePill } from '../AutoSavePill';
import type { AutoSaveStatus } from '../../hooks/useAutoSave';

interface AuthorizationsTabProps {
  patientId: string;
  episodeId?: string | null;
  episodeStartDate?: string | null;
  episodeEndDate?: string | null;
  defaultInsuranceId?: string | null;
  onErrorsChanged?: () => void;
}

type EvaluationStatus = 'idle' | 'loading' | 'success' | 'error';

const STATUS_LABELS: Record<string, string> = Object.fromEntries(
  AUTHORIZATION_STATUS_OPTIONS.map(opt => [opt.value, opt.label])
);

const STATUS_BADGE_VARIANT: Record<
  string,
  'default' | 'secondary' | 'outline' | 'destructive'
> = {
  APPROVED: 'default',
  PENDING: 'secondary',
  IN_PROGRESS: 'secondary',
  DENIED: 'destructive',
  EXPIRED: 'destructive',
  CANCELLED: 'outline',
  NOT_STARTED: 'outline',
  NOT_REQUESTED: 'outline',
};

const INACTIVE_STATUSES = new Set(['DENIED', 'CANCELLED']);

const isActiveAuth = (auth: AuthorizationResponse): boolean =>
  !INACTIVE_STATUSES.has(auth.status ?? '');

const formatDate = (iso: string): string => {
  const parsed = parseISOToLocalDate(iso);
  return parsed ? formatDateToMMDDYYYY(parsed) : '—';
};

export const AuthorizationsTab: React.FC<AuthorizationsTabProps> = ({
  patientId,
  episodeId,
  episodeStartDate,
  episodeEndDate,
  defaultInsuranceId,
  onErrorsChanged,
}) => {
  const { error: showError } = useToast();

  const [authorizations, setAuthorizations] = useState<AuthorizationResponse[]>(
    []
  );
  const [visits, setVisits] = useState<VisitResponse[]>([]);
  const [evaluation, setEvaluation] =
    useState<AuthorizationEvaluationResponse | null>(null);
  const [evaluationStatus, setEvaluationStatus] =
    useState<EvaluationStatus>('idle');
  const evaluationRequestId = useRef(0);
  const evaluationEpisodeId = useRef<string | null | undefined>(undefined);
  const [loading, setLoading] = useState(true);
  const [patientInsurances, setPatientInsurances] = useState<
    PatientInsuranceResponse[]
  >([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingAuthorization, setEditingAuthorization] =
    useState<AuthorizationResponse | null>(null);
  const [movingVisitId, setMovingVisitId] = useState<string | null>(null);
  const [linking, setLinking] = useState(false);
  // Drives Dave's AutoSavePill so link/unlink/auto-link reads as autosaving.
  const [saveStatus, setSaveStatus] = useState<AutoSaveStatus>('idle');

  const refreshEvaluation = useCallback(async () => {
    const requestId = ++evaluationRequestId.current;
    if (evaluationEpisodeId.current !== episodeId) {
      setEvaluation(null);
      evaluationEpisodeId.current = episodeId;
    }
    if (!episodeId) {
      setEvaluationStatus('idle');
      return;
    }
    setEvaluationStatus('loading');
    try {
      const res = await getAuthorizationEvaluation({
        path: { episode_id: episodeId },
      });
      if (requestId !== evaluationRequestId.current) return;
      if (res.error) {
        console.error('Failed to load authorization evaluation:', res.error);
        setEvaluationStatus('error');
        return;
      }
      setEvaluation(res.data ?? null);
      setEvaluationStatus(res.data ? 'success' : 'error');
    } catch (err) {
      if (requestId !== evaluationRequestId.current) return;
      console.error('Failed to load authorization evaluation:', err);
      setEvaluationStatus('error');
    }
  }, [episodeId]);

  const fetchVisits = useCallback(async () => {
    if (!episodeId) {
      setVisits([]);
      return;
    }
    try {
      // Filtered server-side: a long-tenured patient's full visit history is
      // large and every row carries its visit payload, and this tab only ever
      // looks at one episode.
      const res = await getPatientVisits({
        path: { patient_id: patientId },
        query: { episode_id: episodeId },
      });
      if (res.error) {
        console.error('Failed to load visits:', res.error);
        setVisits([]);
        showError('Unable to load visits. Please try again.');
        return;
      }
      setVisits(res.data ?? []);
    } catch (err) {
      // The generated client returns errors on `res.error` (no throwOnError),
      // so this only fires on an unexpected client/runtime failure.
      console.error('Failed to load visits:', err);
      setVisits([]);
      showError('Unable to load visits. Please try again.');
    }
  }, [patientId, episodeId]);

  const fetchAuthorizations = useCallback(async () => {
    if (!episodeId) {
      setAuthorizations([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const res = await listEpisodeAuthorizations({
        path: { episode_id: episodeId },
      });
      if (res.error) {
        console.error('Failed to load authorizations:', res.error);
        setAuthorizations([]);
        return;
      }
      setAuthorizations(res.data ?? []);
    } catch (err) {
      console.error('Failed to load authorizations:', err);
      setAuthorizations([]);
    } finally {
      setLoading(false);
    }
  }, [episodeId]);

  useEffect(() => {
    fetchAuthorizations();
    fetchVisits();
    refreshEvaluation();
  }, [fetchAuthorizations, fetchVisits, refreshEvaluation]);

  useEffect(() => {
    const loadPatientInsurances = async () => {
      try {
        const res = await listPatientInsurances({
          path: { patient_id: patientId },
        });
        if (res.error) {
          console.error('Failed to load patient insurances:', res.error);
          setPatientInsurances([]);
          return;
        }
        setPatientInsurances(res.data ?? []);
      } catch (err) {
        console.error('Failed to load patient insurances:', err);
        setPatientInsurances([]);
      }
    };
    loadPatientInsurances();
  }, [patientId]);

  const moveVisit = async (visitId: string, authorizationId: string | null) => {
    setMovingVisitId(visitId);
    setSaveStatus('saving');
    try {
      const res = await setVisitAuthorization({
        path: { visit_id: visitId },
        body: { authorization_id: authorizationId },
      });
      if (res.error) {
        console.error('Failed to update visit authorization', res.error);
        setSaveStatus('error');
        // EMR-706: the server explains *why* a link was refused (discipline
        // budget full, discipline not on the authorization). Surfacing only
        // "Failed to update visit" leaves the user with no next action.
        showError(getBusinessErrorMessage(res) ?? 'Failed to update visit');
        return;
      }
      await Promise.all([fetchVisits(), refreshEvaluation()]);
      onErrorsChanged?.();
      setSaveStatus('saved');
    } catch (e) {
      console.error('Failed to update visit authorization', e);
      setSaveStatus('error');
      showError('Failed to update visit');
    } finally {
      setMovingVisitId(null);
    }
  };

  if (!episodeId) {
    return (
      <div className='flex flex-col items-center justify-center py-16 text-muted-foreground'>
        <ShieldCheck className='h-12 w-12 mb-4 opacity-30' />
        <p className='text-base font-medium'>
          Select an episode to view authorizations.
        </p>
      </div>
    );
  }

  const activeAuths = authorizations.filter(isActiveAuth);
  const activeAuthIds = new Set(activeAuths.map(a => a.id));
  const evaluationAvailable =
    evaluationStatus === 'success' && evaluation !== null;
  const errors: AuthorizationError[] = evaluation?.errors ?? [];
  const evaluationsByVisitId = new Map(
    evaluation?.visits.map(visitEvaluation => [
      visitEvaluation.visit_id,
      visitEvaluation,
    ])
  );
  const utilizationsByAuthorizationId = new Map(
    evaluation?.utilizations.map(utilization => [
      utilization.authorization_id,
      utilization,
    ])
  );

  // A visit is "effectively linked" only to an active auth
  const effectivelyLinkedAuthId = (v: VisitResponse): string | null => {
    if (v.authorization_id && activeAuthIds.has(v.authorization_id)) {
      return v.authorization_id;
    }
    return null;
  };

  // Display server-evaluated use counts alongside the authorization budget.
  const usedCounts = (auth: AuthorizationResponse): Record<string, number> => {
    return utilizationsByAuthorizationId.get(auth.id)?.used_visits ?? {};
  };

  // The backend owns link eligibility and the reason a link is unavailable.
  const buildMoveOptions = (v: VisitResponse, currentAuthId: string | null) =>
    (evaluationsByVisitId.get(v.id)?.link_options ?? [])
      .filter(option => option.authorization_id !== currentAuthId)
      .flatMap(option => {
        const authorization = authorizations.find(
          auth => auth.id === option.authorization_id
        );
        if (!authorization) return [];
        return [
          {
            authId: authorization.id,
            label: authorization.authorization_number
              ? `#${authorization.authorization_number}`
              : `${formatDate(authorization.start_date)}–${formatDate(authorization.end_date)}`,
            reason: option.reason,
          },
        ];
      });

  const autoLinkable = evaluation?.auto_link_suggestions ?? [];

  const handleAutoLink = async () => {
    const assignments = autoLinkable;
    if (assignments.length === 0) return;
    setLinking(true);
    setSaveStatus('saving');
    let failed = false;
    try {
      for (const { visit_id, authorization_id } of assignments) {
        const res = await setVisitAuthorization({
          path: { visit_id },
          body: { authorization_id },
        });
        if (res.error) {
          console.error('Auto-link failed for visit', visit_id, res.error);
          // EMR-706: report the server's reason for the visit that stopped the
          // run, not just that "some" failed -- otherwise a budget block is
          // indistinguishable from an outage.
          showError(
            getBusinessErrorMessage(res) ??
              'Some visits could not be auto-linked'
          );
          failed = true;
          break;
        }
      }
      await Promise.all([fetchVisits(), refreshEvaluation()]);
      onErrorsChanged?.();
      setSaveStatus(failed ? 'error' : 'saved');
    } catch (e) {
      console.error('Auto-link failed', e);
      setSaveStatus('error');
      showError('Failed to auto-link visits');
    } finally {
      setLinking(false);
    }
  };

  // Build checks panel items
  const hasActiveAuth = activeAuths.length > 0;
  const checkItems = [
    // Pass row: "Authorization on file" when at least one active auth exists
    ...(hasActiveAuth
      ? [
          {
            key: 'auth-on-file',
            status: 'pass' as const,
            message: 'Authorization on file',
            anchor: undefined,
          },
        ]
      : []),
    // Fail rows from backend errors (index keeps keys unique when code +
    // authorization_id repeat, e.g. SN and PT frequency_exceeded on one auth).
    ...errors.map((err, i) => ({
      key: `${err.code}-${err.visit_id ?? ''}-${err.authorization_id ?? ''}-${i}`,
      status: 'fail' as const,
      message: err.message,
      anchor: errorToAnchor(err),
    })),
  ];

  const visitErrorIds = new Set(
    errors.filter(e => e.visit_id).map(e => e.visit_id!)
  );

  const unlinkedVisits = visits.filter(
    v => effectivelyLinkedAuthId(v) === null
  );

  return (
    <TooltipProvider>
      <div className='space-y-3'>
        {evaluationStatus === 'error' && (
          <div className='flex items-center gap-2 rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-sm text-destructive'>
            <AlertTriangle className='h-4 w-4 shrink-0' />
            Unable to load authorization usage
          </div>
        )}

        {/* Checks panel */}
        {checkItems.length > 0 && (
          <Card>
            <CardHeader className='pb-2 pt-3 px-3'>
              <CardTitle className='text-sm font-semibold'>
                Authorization checks
              </CardTitle>
            </CardHeader>
            <CardContent className='px-3 pb-3 space-y-1'>
              {checkItems.map(item => (
                <button
                  key={item.key}
                  type='button'
                  disabled={!item.anchor}
                  onClick={() => {
                    if (item.anchor) {
                      scrollToField(item.anchor, { focus: false });
                    }
                  }}
                  className={[
                    'flex w-full items-center gap-2 rounded px-2 py-1 text-left text-sm',
                    item.anchor
                      ? 'cursor-pointer hover:bg-muted/40'
                      : 'cursor-default',
                    item.status === 'fail'
                      ? 'text-destructive'
                      : 'text-green-700',
                  ].join(' ')}
                >
                  {item.status === 'pass' ? (
                    <Check className='h-3.5 w-3.5 shrink-0' />
                  ) : (
                    <AlertTriangle className='h-3.5 w-3.5 shrink-0' />
                  )}
                  <span>{item.message}</span>
                </button>
              ))}
            </CardContent>
          </Card>
        )}

        {/* Authorization create / header */}
        <div className='overflow-hidden rounded-md border border-border/60 bg-background/40'>
          <div className='flex items-center justify-between gap-2 border-b border-border/60 bg-muted/20 px-3 py-1.5'>
            {/* Autosave status — links/unlinks save immediately */}
            <AutoSavePill status={saveStatus} variant='autosave' />
            <Button
              size='sm'
              variant='outline'
              onClick={() => {
                setEditingAuthorization(null);
                setDialogOpen(true);
              }}
            >
              <Plus className='h-3.5 w-3.5 mr-1' />
              Add Authorization
            </Button>
          </div>

          {loading ||
          (evaluationStatus === 'loading' && evaluation === null) ? (
            <div className='py-8 text-center text-sm text-muted-foreground'>
              Loading…
            </div>
          ) : authorizations.length === 0 ? (
            <div className='flex flex-col items-center justify-center py-16 text-muted-foreground'>
              <ShieldCheck className='h-12 w-12 mb-4 opacity-30' />
              <p className='text-base font-medium'>
                No authorizations for this episode
              </p>
            </div>
          ) : (
            <div className='divide-y divide-border/60'>
              {/* Active authorizations — grouped view */}
              {activeAuths.map(auth => {
                const authVisits = visits.filter(
                  v => effectivelyLinkedAuthId(v) === auth.id
                );
                const used = usedCounts(auth);

                return (
                  <div
                    key={auth.id}
                    data-field-anchor={`auth-${auth.id}`}
                    className='px-3 py-2.5'
                  >
                    {/* Auth card header */}
                    <div className='flex flex-wrap items-start justify-between gap-2'>
                      <div className='min-w-0 space-y-1'>
                        {/* Date range + status */}
                        <div className='flex flex-wrap items-center gap-2'>
                          <span className='text-sm font-semibold'>
                            {formatDate(auth.start_date)} –{' '}
                            {formatDate(auth.end_date)}
                          </span>
                          <Badge
                            variant={
                              STATUS_BADGE_VARIANT[
                                (auth.status ??
                                  'NOT_STARTED') as AuthorizationStatus
                              ] ?? 'outline'
                            }
                          >
                            {STATUS_LABELS[auth.status ?? 'NOT_STARTED'] ??
                              auth.status}
                          </Badge>
                          {(auth.documents?.length || auth.document_id) && (
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Paperclip className='h-3.5 w-3.5 text-muted-foreground' />
                              </TooltipTrigger>
                              <TooltipContent>
                                {auth.documents?.length || 1} attachment(s)
                              </TooltipContent>
                            </Tooltip>
                          )}
                        </div>
                        {/* Labelled fields */}
                        <div className='flex flex-wrap items-center gap-x-4 gap-y-0.5 text-xs text-muted-foreground'>
                          <span>
                            <span className='font-medium text-foreground/70'>
                              Insurance:{' '}
                            </span>
                            {auth.insurance_name ?? '—'}
                          </span>
                          <span>
                            <span className='font-medium text-foreground/70'>
                              Auth #:{' '}
                            </span>
                            {auth.authorization_number ?? '—'}
                          </span>
                        </div>
                        {/* Per-discipline used / allowed */}
                        <div className='flex flex-wrap items-center gap-1 pt-0.5'>
                          <span className='mr-0.5 text-[11px] font-medium text-foreground/60'>
                            Visits used:
                          </span>
                          {DISCIPLINE_VISIT_FIELDS.filter(
                            f =>
                              auth[f.key] !== null && auth[f.key] !== undefined
                          ).map(f => {
                            const allowed = (auth[f.key] as number | null) ?? 0;
                            const usedCount = used[f.discipline];
                            if (evaluationStatus === 'error') {
                              return (
                                <Badge key={f.key} variant='secondary'>
                                  {f.label} Unavailable
                                </Badge>
                              );
                            }
                            const displayedUsedCount = usedCount ?? 0;
                            const isOver = displayedUsedCount > allowed;
                            return (
                              <Tooltip key={f.key}>
                                <TooltipTrigger asChild>
                                  <Badge
                                    variant={
                                      isOver ? 'destructive' : 'secondary'
                                    }
                                  >
                                    {f.label} {displayedUsedCount}/{allowed}
                                  </Badge>
                                </TooltipTrigger>
                                <TooltipContent>
                                  {f.full}: {displayedUsedCount} of {allowed}{' '}
                                  visits used
                                  {isOver ? ' (over limit)' : ''}
                                </TooltipContent>
                              </Tooltip>
                            );
                          })}
                        </div>
                      </div>
                      {/* Row actions */}
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            size='sm'
                            variant='ghost'
                            onClick={() => {
                              setEditingAuthorization(auth);
                              setDialogOpen(true);
                            }}
                          >
                            <Pencil className='h-3.5 w-3.5 mr-1' />
                            Edit
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>
                          Edit or delete authorization
                        </TooltipContent>
                      </Tooltip>
                    </div>

                    {/* Linked visits */}
                    <div className='mt-1 ml-2 space-y-0.5'>
                      {authVisits.length === 0 ? (
                        <p className='text-xs text-muted-foreground pl-2 py-1'>
                          No visits linked
                        </p>
                      ) : (
                        authVisits.map(v => (
                          <VisitRow
                            key={v.id}
                            visit={v}
                            currentAuthId={auth.id}
                            currentAuthLabel={authorizationLabel(auth)}
                            moveOptions={buildMoveOptions(v, auth.id)}
                            consumesSlot={
                              evaluationsByVisitId.get(v.id)?.consumes_slot ??
                              null
                            }
                            evaluationStatus={evaluationStatus}
                            hasError={visitErrorIds.has(v.id)}
                            isMoving={movingVisitId === v.id}
                            actionsAvailable={evaluationAvailable}
                            onMove={moveVisit}
                          />
                        ))
                      )}
                    </div>
                  </div>
                );
              })}

              {/* Inactive (denied/cancelled) auths — read-only rows */}
              {authorizations
                .filter(a => !isActiveAuth(a))
                .map(auth => (
                  <div
                    key={auth.id}
                    className='grid grid-cols-1 lg:grid-cols-[180px_110px_1fr_150px_1.4fr_60px] gap-x-3 gap-y-1 px-3 py-2.5 text-sm cursor-pointer hover:bg-muted/30 transition-colors opacity-60'
                    onClick={() => {
                      setEditingAuthorization(auth);
                      setDialogOpen(true);
                    }}
                  >
                    <span className='font-medium'>
                      {formatDate(auth.start_date)} –{' '}
                      {formatDate(auth.end_date)}
                    </span>
                    <span>
                      <Badge
                        variant={
                          STATUS_BADGE_VARIANT[
                            (auth.status ??
                              'NOT_STARTED') as AuthorizationStatus
                          ] ?? 'outline'
                        }
                        className='text-[10px]'
                      >
                        {STATUS_LABELS[auth.status ?? 'NOT_STARTED'] ??
                          auth.status}
                      </Badge>
                    </span>
                    <span className='truncate text-muted-foreground'>
                      {auth.insurance_name ?? '—'}
                    </span>
                    <span className='truncate'>
                      {auth.authorization_number ?? '—'}
                    </span>
                    <span />
                    <span>
                      {(auth.documents?.length || auth.document_id) && (
                        <Paperclip className='h-4 w-4 text-muted-foreground' />
                      )}
                    </span>
                  </div>
                ))}
            </div>
          )}
        </div>

        {/* Unlinked visits group */}
        {visits.length > 0 && (
          <div
            data-field-anchor='auth-unlinked'
            className='overflow-hidden rounded-md border border-border/60 bg-background/40'
          >
            <div className='flex items-center justify-between gap-2 border-b border-border/60 bg-muted/20 px-3 py-1.5'>
              <span className='flex items-center text-sm font-medium text-muted-foreground'>
                Unlinked visits
                {unlinkedVisits.length > 0 && (
                  <Badge variant='secondary' className='ml-2'>
                    {unlinkedVisits.length}
                  </Badge>
                )}
              </span>
              {unlinkedVisits.length > 0 && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    {/* span keeps the tooltip working while the button is disabled */}
                    <span>
                      <Button
                        size='sm'
                        variant='outline'
                        disabled={
                          !evaluationAvailable ||
                          linking ||
                          autoLinkable.length === 0
                        }
                        onClick={handleAutoLink}
                      >
                        <Link2 className='h-3.5 w-3.5 mr-1' />
                        {linking
                          ? 'Auto-linking…'
                          : `Auto-link${autoLinkable.length ? ` (${autoLinkable.length})` : ''}`}
                      </Button>
                    </span>
                  </TooltipTrigger>
                  <TooltipContent>
                    {evaluationStatus === 'loading'
                      ? 'Checking authorization availability…'
                      : evaluationStatus === 'error'
                        ? "Authorization usage couldn't be loaded"
                        : autoLinkable.length === 0
                          ? 'No unlinked visits can be safely linked to an existing authorization'
                          : `Link ${autoLinkable.length} visit${autoLinkable.length === 1 ? '' : 's'} into open, in-window authorization slots`}
                  </TooltipContent>
                </Tooltip>
              )}
            </div>
            <div className='divide-y divide-border/60'>
              {unlinkedVisits.length === 0 ? (
                <p className='px-3 py-4 text-sm text-muted-foreground text-center'>
                  All visits are linked to an authorization.
                </p>
              ) : (
                unlinkedVisits.map(v => (
                  <VisitRow
                    key={v.id}
                    visit={v}
                    currentAuthId={null}
                    currentAuthLabel={null}
                    moveOptions={buildMoveOptions(v, null)}
                    consumesSlot={
                      evaluationsByVisitId.get(v.id)?.consumes_slot ?? null
                    }
                    evaluationStatus={evaluationStatus}
                    hasError={visitErrorIds.has(v.id)}
                    isMoving={movingVisitId === v.id}
                    actionsAvailable={evaluationAvailable}
                    onMove={moveVisit}
                  />
                ))
              )}
            </div>
          </div>
        )}

        <AuthorizationDialog
          open={dialogOpen}
          onClose={() => {
            setDialogOpen(false);
            setEditingAuthorization(null);
          }}
          patientId={patientId}
          episodeId={episodeId}
          episodeStartDate={episodeStartDate}
          episodeEndDate={episodeEndDate}
          patientInsurances={patientInsurances}
          defaultInsuranceId={defaultInsuranceId}
          editingAuthorization={editingAuthorization}
          episodeVisits={visits}
          onSaved={async () => {
            // Creating/editing/deleting an auth changes budgets, dates, status, and
            // (on delete) visit linkage — refresh visits + checks panel + parent badge too.
            await Promise.all([
              fetchAuthorizations(),
              fetchVisits(),
              refreshEvaluation(),
            ]);
            onErrorsChanged?.();
          }}
        />
      </div>
    </TooltipProvider>
  );
};

// ─── VisitRow sub-component ───────────────────────────────────────────────────

interface MoveOption {
  authId: string;
  label: string;
  reason: string | null; // null = valid target; otherwise why it's blocked
}

interface VisitRowProps {
  visit: VisitResponse;
  currentAuthId: string | null;
  currentAuthLabel: string | null;
  moveOptions: MoveOption[];
  consumesSlot: boolean | null;
  evaluationStatus: EvaluationStatus;
  hasError: boolean;
  isMoving: boolean;
  actionsAvailable: boolean;
  onMove: (visitId: string, authorizationId: string | null) => void;
}

const VisitRow: React.FC<VisitRowProps> = ({
  visit,
  currentAuthId,
  currentAuthLabel,
  moveOptions,
  consumesSlot,
  evaluationStatus,
  hasError,
  isMoving,
  actionsAvailable,
  onMove,
}) => {
  const validTargets = moveOptions.filter(o => o.reason === null);
  const blockedTargets = moveOptions.filter(o => o.reason !== null);
  const canUnlink = currentAuthId !== null;
  const hasAction = actionsAvailable && (moveOptions.length > 0 || canUnlink);
  const isMissed = visit.missed_visit_data != null;

  const blockerSummary =
    evaluationStatus === 'loading'
      ? 'Checking authorization availability…'
      : evaluationStatus === 'error'
        ? "Authorization usage couldn't be loaded"
        : isMissed
          ? "Missed visits don't use an authorized visit"
          : consumesSlot === false
            ? "This visit type doesn't use an authorized visit"
            : moveOptions.length === 0
              ? 'No active authorizations are available for this visit'
              : Array.from(
                  new Set(blockedTargets.map(o => o.reason as string))
                ).join('; ');

  const trigger = (
    <Button size='sm' variant='ghost' disabled={isMoving || !hasAction}>
      Move <ChevronDown className='h-3 w-3 ml-0.5' />
    </Button>
  );

  return (
    <div
      data-field-anchor={`visit-${visit.id}`}
      className={[
        'flex items-center gap-2 px-2 py-1.5 text-sm rounded',
        isMoving ? 'opacity-50 pointer-events-none' : '',
      ].join(' ')}
    >
      {/* Discipline chip */}
      {visit.discipline && (
        <Badge variant='outline' className='shrink-0'>
          {visit.discipline}
        </Badge>
      )}

      {/* Visit label, with the missed marker beside it — a missed visit spends
          no authorized visit and is unlinked automatically, so the row needs to
          say why it moved */}
      <div className='flex flex-1 items-center gap-2 min-w-0'>
        <span className='truncate text-xs text-muted-foreground'>
          {visit.visit_type_display_name}
        </span>
        {isMissed && (
          <Badge variant='destructive' className='shrink-0 whitespace-nowrap'>
            Missed
          </Badge>
        )}
      </div>

      {/* Visit date */}
      <span className='text-xs text-muted-foreground shrink-0'>
        {visit.visit_date ? formatDateToMMDDYYYY(visit.visit_date) : '—'}
      </span>

      {/* Error marker */}
      {hasError && (
        <AlertTriangle className='h-3.5 w-3.5 text-destructive shrink-0' />
      )}

      {/* Move menu — disabled with a tooltip when no action is available */}
      {hasAction ? (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>{trigger}</DropdownMenuTrigger>
          <DropdownMenuContent align='end' className='max-w-xs'>
            {currentAuthId !== null && (
              <>
                <DropdownMenuItem disabled>
                  <Check className='h-3 w-3 mr-1.5' />
                  {currentAuthLabel ?? 'Current auth'} (current)
                </DropdownMenuItem>
                <DropdownMenuSeparator />
              </>
            )}

            {validTargets.map(o => (
              <DropdownMenuItem
                key={o.authId}
                onSelect={() => onMove(visit.id, o.authId)}
              >
                Move to {o.label}
              </DropdownMenuItem>
            ))}

            {blockedTargets.map(o => (
              <DropdownMenuItem
                key={o.authId}
                disabled
                className='flex flex-col items-start gap-0'
              >
                <span className='flex items-center gap-1'>
                  <AlertTriangle className='h-3 w-3 text-amber-600 shrink-0' />
                  Move to {o.label}
                </span>
                <span className='text-[10px] text-muted-foreground'>
                  {o.reason}
                </span>
              </DropdownMenuItem>
            ))}

            {canUnlink && (
              <>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  className='text-destructive focus:text-destructive'
                  onSelect={() => onMove(visit.id, null)}
                >
                  Unlink (make orphan)
                </DropdownMenuItem>
              </>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      ) : (
        <Tooltip>
          {/* span wrapper keeps the tooltip active over the disabled button */}
          <TooltipTrigger asChild>
            <span tabIndex={0}>{trigger}</span>
          </TooltipTrigger>
          <TooltipContent className='max-w-xs'>
            Can&apos;t link this visit: {blockerSummary}
          </TooltipContent>
        </Tooltip>
      )}
    </div>
  );
};
